/**
 * Browser push opt-in (5.4b). Registers the service worker, subscribes to Web Push with the server's VAPID
 * key, and posts the subscription (+ the device token, so the server binds it to your profile). If the
 * server has no VAPID keys configured, it degrades gracefully to the LOCAL Notification API — notifications
 * are shown by the page itself (see localNotify) for raid/whale/sale events while the tab is backgrounded.
 */
import { resolveServerUrl } from './config.js';
import { telemetryPushOptin } from './telemetry.js';

const LOCAL_FLAG = 'soh_local_notify'; // set when we fell back to the local Notification API
const TOKEN_KEY = 'soh_device_token';  // the SAME device token the net layer uses (→ server profileId)

/** HTTP base of the game server (ws(s):// → http(s)://), where the /push/* endpoints live. */
function httpBase(): string {
  try { return resolveServerUrl().replace(/^ws/, 'http').replace(/\/+$/, ''); } catch { return ''; }
}
function deviceToken(): string { try { return localStorage.getItem(TOKEN_KEY) ?? ''; } catch { return ''; } }

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'Notification' in window;
}

/** 'on' = subscribed (server push or local), 'off' = not enabled, 'blocked' = permission denied, 'unsupported'. */
export async function pushStatus(): Promise<'on' | 'off' | 'blocked' | 'unsupported'> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) return 'on';
  } catch { /* fall through */ }
  if (localStorage.getItem(LOCAL_FLAG) === '1' && Notification.permission === 'granted') return 'on';
  return 'off';
}

/** Opt in: register the SW, ask permission, subscribe to server push (or fall back to local). */
export async function enablePush(): Promise<{ ok: boolean; mode: 'push' | 'local' | 'none'; note: string }> {
  if (!pushSupported()) return { ok: false, mode: 'none', note: 'Notifications are not supported on this device.' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, mode: 'none', note: 'Notifications were blocked in the browser.' };
  const base = httpBase();
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    const vapid = await fetch(`${base}/push/vapid`).then((r) => r.json()).catch(() => ({ enabled: false, key: '' }));
    if (vapid?.enabled && vapid.key) {
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapid.key) as BufferSource });
      const json = sub.toJSON();
      await fetch(`${base}/push/subscribe`, { method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token: deviceToken(), sub: { endpoint: json.endpoint, keys: json.keys } }) });
      localStorage.removeItem(LOCAL_FLAG);
      telemetryPushOptin(); // P1 funnel: the captain opted in to notifications (once; no PII)
      return { ok: true, mode: 'push', note: '🔔 Browser notifications enabled — raids, sales & the White Whale.' };
    }
  } catch { /* server push failed → local fallback below */ }
  // graceful degrade: local Notification API (shown by the page for events while backgrounded)
  localStorage.setItem(LOCAL_FLAG, '1');
  telemetryPushOptin(); // P1 funnel: opted in (local fallback still counts as an opt-in; once; no PII)
  return { ok: true, mode: 'local', note: '🔔 Notifications on (this device only) — server push is not configured.' };
}

export async function disablePush(): Promise<void> {
  localStorage.removeItem(LOCAL_FLAG);
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) { await sub.unsubscribe(); await fetch(`${httpBase()}/push/unsubscribe`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token: deviceToken() }) }).catch(() => {}); }
  } catch { /* best-effort */ }
}

/** Local-fallback notification: shown by the page for an event while backgrounded (no server push). */
export function localNotify(title: string, body: string): void {
  try {
    if (localStorage.getItem(LOCAL_FLAG) !== '1') return;
    if (Notification.permission !== 'granted') return;
    if (typeof document !== 'undefined' && !document.hidden) return; // in-game toasts already cover the foreground
    new Notification(title, { body, icon: '/menu/crest.webp', tag: 'sea-of-robin' });
  } catch { /* noop */ }
}
